import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { BookOpen, Check, Loader2, Target } from "lucide-react";

/** El temario del nivel del estudiante: lo que ya vio y lo que le falta. */
export default function MyLevelPlan() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api.get("/my-level-plan")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, []);

  if (user && !user.study_plan_access) {
    return (
      <Card className="p-10 text-center" data-testid="my-plan-locked">
        <BookOpen className="h-7 w-7 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-600 font-medium">{t("splan_none")}</p>
      </Card>
    );
  }

  if (cargando) {
    return <Card className="p-10 text-center" data-testid="my-plan-loading">
      <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></Card>;
  }

  if (!data?.plan) {
    return (
      <Card className="p-10 text-center" data-testid="my-plan-empty">
        <BookOpen className="h-7 w-7 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-600 font-medium">{t("splan_none")}</p>
      </Card>
    );
  }

  const pct = data.units_total ? Math.round((data.units_done / data.units_total) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="my-plan-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-brand-blue" /> {t("splan_title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("splan_sub")}</p>
      </div>

      <div className="rounded-2xl bg-[#0d1b2a] text-white p-6">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="font-semibold text-lg tracking-tight" data-testid="my-plan-level">
            {data.plan.label}
          </h3>
        </div>
        <div className="h-[7px] rounded-full bg-white/15 overflow-hidden mt-5">
          <div className="h-full rounded-full bg-gradient-to-r from-[#ff731d] to-[#ff9f5a] transition-all duration-700"
               style={{ width: `${pct}%` }} data-testid="my-plan-bar" />
        </div>
        <div className="flex gap-8 mt-4 flex-wrap text-[13px]">
          <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{t("splan_seen")}</span>
            <b data-testid="my-plan-done">{data.units_done} / {data.units_total}</b></div>
          <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{t("splan_left")}</span>
            <b>{data.units_total - data.units_done}</b></div>
        </div>
      </div>

      <div>
        {data.units.map((u, i) => (
          <Card key={u.id}
                className={`mb-2.5 p-4 flex items-start gap-3 ${u.done ? "bg-emerald-50/40" : ""}
                  ${u.id === data.current_unit_id ? "border-[#ff731d]" : ""}`}
                data-testid={`my-plan-unit-${u.id}`}>
            <span className={`h-7 w-7 rounded-lg grid place-items-center text-[12px] font-extrabold shrink-0
              ${u.done ? "bg-emerald-100 text-emerald-700"
                : u.id === data.current_unit_id ? "bg-[#ff731d] text-white" : "bg-slate-100 text-slate-500"}`}>
              {u.done ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-900">{u.title}</span>
                {u.done && <span className="text-[11px] font-bold rounded-full px-2 py-[3px] bg-emerald-100 text-emerald-700">
                  {t("splan_done")}</span>}
                {!u.done && u.id === data.current_unit_id && (
                  <span className="text-[11px] font-bold rounded-full px-2 py-[3px] bg-orange-100 text-orange-700">
                    {t("splan_current")}</span>
                )}
              </div>
              {!!u.objectives?.length && (
                <div className="mt-2">
                  <h5 className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-1 flex items-center gap-1.5">
                    <Target className="h-3 w-3" /> {t("splan_can")}
                  </h5>
                  <ul className="list-disc pl-4 text-[13px] text-slate-600 leading-7">
                    {u.objectives.map((o, k) => <li key={k}>{o}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
